/**
 * useValidation - Live validation hook
 * Issue #1101: Centralized live validation system
 *
 * Validates input values against centralized rules with debouncing.
 */

import { useState, useEffect, useRef } from 'react';
import { validateValue, type ValidationRuleType } from '../lib/validation/rules';

export type ValidationStatus = 'idle' | 'valid' | 'invalid';

export interface UseValidationOptions {
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Validate immediately on mount if value exists */
  validateOnMount?: boolean;
  /** Additional validation rules to combine */
  additionalRules?: ValidationRuleType[];
}

export interface UseValidationResult {
  /** Current validation status */
  status: ValidationStatus;
  /** Error or success message */
  message: string | null;
  /** Whether the field has been touched (user has interacted) */
  touched: boolean;
  /** Mark field as touched */
  setTouched: (touched: boolean) => void;
  /** Force re-validation */
  validate: () => void;
}

/**
 * Hook for live input validation
 *
 * @param value - The current input value
 * @param ruleType - The validation rule type from the registry
 * @param options - Optional configuration
 * @returns Validation state and helpers
 *
 * @example
 * ```tsx
 * const { status, message } = useValidation(email, 'email');
 *
 * // Show error if invalid
 * {status === 'invalid' && <span className="text-oro-error">{message}</span>}
 * ```
 */
export function useValidation(
  value: string,
  ruleType: ValidationRuleType,
  options: UseValidationOptions = {}
): UseValidationResult {
  const { debounceMs = 300, validateOnMount = false, additionalRules = [] } = options;

  const [status, setStatus] = useState<ValidationStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialValueRef = useRef(value);

  // Validation function
  const runValidation = (val: string) => {
    // Empty values are idle (use 'required' rule to enforce)
    if (!val || val.trim() === '') {
      setStatus('idle');
      setMessage(null);
      return;
    }

    // Check primary rule
    const result = validateValue(val, ruleType);

    // Check additional rules if primary passes
    if (result.isValid && additionalRules.length > 0) {
      for (const additionalRule of additionalRules) {
        const additionalResult = validateValue(val, additionalRule);
        if (!additionalResult.isValid) {
          setStatus('invalid');
          setMessage(additionalResult.message);
          return;
        }
      }
    }

    setStatus(result.isValid ? 'valid' : 'invalid');
    setMessage(result.message);
  };

  // Debounced validation effect
  useEffect(() => {
    // Skip validation on mount unless explicitly requested
    if (!validateOnMount && value === initialValueRef.current && !touched) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set idle while typing (debouncing)
    if (status !== 'idle' && value !== '') {
      // Keep current status during debounce to avoid flickering
    }

    // Debounce the validation
    timeoutRef.current = setTimeout(() => {
      runValidation(value);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, ruleType, debounceMs, validateOnMount, touched]);

  // Force validation function
  const validate = () => {
    setTouched(true);
    runValidation(value);
  };

  return {
    status,
    message,
    touched,
    setTouched,
    validate,
  };
}

/**
 * Hook for validating required fields
 * Combines any rule type with the 'required' rule
 */
export function useRequiredValidation(
  value: string,
  ruleType: ValidationRuleType,
  options: UseValidationOptions = {}
): UseValidationResult {
  return useValidation(value, ruleType, {
    ...options,
    additionalRules: ['required', ...(options.additionalRules || [])],
  });
}

export default useValidation;
